import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/** @typedef {{ key: string; product_id: number; variant_id: number; quantity: number }} CartLineItem */
/** @typedef {{ items: CartLineItem[] }} CartState */

const DISMISSED_STORAGE_KEY = 'cart_warranty_dismissed';

/**
 * Module-level FIFO queue that serializes EVERY warranty mutation across
 * all cart-warranty instances (drawer + cart page) and all sources
 * (mount, cart:update event, radio click, remove button).
 *
 * Each enqueued operation waits for the previous one to fully resolve —
 * including the underlying server request — before starting. This is the
 * core protection against the race that produced duplicate warranty lines
 * (e.g. auto-add running while the customer clicks a different variant).
 *
 * @type {Promise<void>}
 */
let opChain = Promise.resolve();

/**
 * Enqueues a warranty operation. Operations are awaited in FIFO order and
 * the chain never rejects (errors are swallowed so a single failure does
 * not block subsequent operations).
 * @template T
 * @param {() => Promise<T>} op
 * @returns {Promise<T | undefined>}
 */
function enqueue(op) {
  const next = opChain.then(op, op);
  opChain = next.then(
    () => {},
    () => {}
  );
  return next.catch(() => undefined);
}

/**
 * Web component that manages warranty selection in the cart.
 *
 * Guarantees:
 *   - at most ONE warranty line in the cart at any time;
 *   - the default (cheapest) variant is auto-added when missing, unless
 *     the customer dismissed warranty for the session;
 *   - the warranty is auto-removed when no real products remain.
 *
 * All mutations are serialized through a shared queue so concurrent
 * sources (auto-add, radio click, remove, external cart:update) can never
 * produce duplicates.
 */
class CartWarranty extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#initWarranty();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Re-runs the warranty init flow when the cart is updated elsewhere
   * (e.g. customer adds a product from a PDP, removes the last real
   * product, etc.).
   */
  #handleCartUpdate = () => {
    this.#initWarranty();
  };

  /** @returns {number} */
  get #defaultVariantId() {
    return Number(this.dataset.defaultVariantId) || 0;
  }

  /** @returns {number} */
  get #warrantyProductId() {
    return Number(this.dataset.warrantyProductId);
  }

  /**
   * Fetches the current cart state.
   * @returns {Promise<CartState>}
   */
  async #getCart() {
    const res = await fetch(`${Theme.routes.cart_url}.js`);
    return res.json();
  }

  /**
   * Returns every warranty line item currently in the cart.
   * @param {CartLineItem[]} items
   * @returns {CartLineItem[]}
   */
  #findAllWarrantyItems(items) {
    return items.filter((item) => item.product_id === this.#warrantyProductId);
  }

  /**
   * Removes one or more cart line items by key in a single request.
   * Using keys (instead of line indexes) avoids index drift when
   * multiple lines are removed at once.
   * @param {string[]} keys
   */
  async #removeLinesByKey(keys) {
    if (keys.length === 0) return;
    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify({ updates }) })
    );
  }

  /**
   * Init flow run on mount and on every cart:update event.
   * All steps share the same queue, so a concurrent click never overlaps.
   */
  #initWarranty() {
    return enqueue(async () => {
      await this.#consolidateUnsafe();
      await this.#cleanOrphanedUnsafe();
      await this.#autoAddDefaultUnsafe();
    });
  }

  /**
   * Guarantees at most ONE warranty line in the cart. If more are found,
   * extras (all but the first) are removed.
   *
   * "Unsafe" = must be called from within the queue. Public callers go
   * through #initWarranty / selectWarranty / removeWarranty.
   */
  async #consolidateUnsafe() {
    try {
      const cart = await this.#getCart();
      const warranties = this.#findAllWarrantyItems(cart.items);
      if (warranties.length <= 1) return;

      const extraKeys = warranties.slice(1).map((item) => item.key);
      await this.#removeLinesByKey(extraKeys);
      this.#rerenderAllSections();
    } catch {
      // Fail silently — cart will still work
    }
  }

  /**
   * If the cart contains only warranty items (no real products),
   * remove every warranty line.
   */
  async #cleanOrphanedUnsafe() {
    try {
      const cart = await this.#getCart();
      const hasNonWarranty = cart.items.some((item) => item.product_id !== this.#warrantyProductId);
      if (hasNonWarranty) return;

      const warrantyKeys = this.#findAllWarrantyItems(cart.items).map((item) => item.key);
      if (warrantyKeys.length === 0) return;

      await this.#removeLinesByKey(warrantyKeys);
      this.#rerenderAllSections();
    } catch {
      // Fail silently — cart will still work
    }
  }

  /**
   * Auto-adds the default (cheapest) warranty variant when:
   *   - a default variant is configured in the snippet;
   *   - the customer hasn't dismissed warranty in this session;
   *   - there's at least one non-warranty product in the cart;
   *   - and no warranty is already present.
   *
   * After adding, runs one extra consolidate as a belt-and-suspenders
   * guard against any duplicate that may have slipped through on the
   * server side.
   */
  async #autoAddDefaultUnsafe() {
    const variantId = this.#defaultVariantId;
    if (!variantId) return;
    if (sessionStorage.getItem(DISMISSED_STORAGE_KEY) === '1') return;

    try {
      const cart = await this.#getCart();
      if (this.#findAllWarrantyItems(cart.items).length > 0) return;

      const hasNonWarranty = cart.items.some((item) => item.product_id !== this.#warrantyProductId);
      if (!hasNonWarranty) return;

      await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        })
      );

      await this.#consolidateUnsafe();
      this.#rerenderAllSections();
    } catch {
      // Fail silently — cart will still work
    }
  }

  /**
   * Handles warranty variant selection via radio buttons.
   * Removes ANY existing warranty before adding the newly selected one,
   * then re-consolidates as a defensive guard.
   * @param {Event} event
   */
  selectWarranty = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const variantId = Number(input.value);
    sessionStorage.removeItem(DISMISSED_STORAGE_KEY);

    enqueue(async () => {
      try {
        const cart = await this.#getCart();
        const existing = this.#findAllWarrantyItems(cart.items);
        const [first] = existing;

        if (existing.length === 1 && first?.variant_id === variantId) return;

        if (existing.length > 0) {
          await this.#removeLinesByKey(existing.map((item) => item.key));
        }

        await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          })
        );

        await this.#consolidateUnsafe();
        this.#rerenderAllSections();
      } catch (error) {
        console.error(error);
      }
    });
  };

  /**
   * Removes every warranty line from the cart and marks warranty as
   * dismissed for the session (so auto-add won't re-add it).
   * @param {Event} event
   */
  removeWarranty = (event) => {
    event.preventDefault();
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, '1');

    enqueue(async () => {
      try {
        const cart = await this.#getCart();
        const keys = this.#findAllWarrantyItems(cart.items).map((item) => item.key);
        if (keys.length === 0) return;

        await this.#removeLinesByKey(keys);
        this.#rerenderAllSections();
      } catch (error) {
        console.error(error);
      }
    });
  };

  /**
   * Re-renders every cart-items-component section on the page
   * (covers both cart page and cart drawer).
   */
  #rerenderAllSections() {
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionRenderer.renderSection(el.dataset.sectionId, { cache: false });
      }
    });
  }
}

if (!customElements.get('cart-warranty-component')) {
  customElements.define('cart-warranty-component', CartWarranty);
}
